import { index, pgTable, text, timestamp, uuid, varchar, boolean } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { id } from './_shared';
import { users } from './users';

// =============================================================================
// EMAIL GATEWAY — the inbound door (docs/design/email-door-2026-07-05.md).
// GATEWAY-API lane, 2026-07-05. Three tables, current-law shape throughout
// (real uuid FK both sides, ON DELETE CASCADE — mirrors drive_export.ts /
// presence_profiles.ts, the newest precedent, NOT the older text-keyed
// user_id convention CLAUDE.md names as migration debt on jobs/watchlistSources
// etc; this is a brand-new table built right the first time).
//
// THE THREE TABLES:
//   user_email_tokens — the per-user secret token half of the two-factor
//     identity check (DMARC pass + token match + registered-From match, ALL
//     THREE required — see the design doc's "security model" section).
//     Regenerable: the OLD row is REVOKED (revoked_at stamped), never
//     deleted — nothing-unrecoverable — and a fresh row is inserted. A user
//     can therefore have MULTIPLE historical rows; exactly one live
//     (revoked_at IS NULL) row at a time is the invariant the application
//     enforces (revoke-then-insert, same transaction-adjacent shape as
//     discoveryAccounts' existing/update-or-insert pattern in auth.routes.ts).
//   login_links — the magic-link redemption records (P3 of the design doc).
//     Single-use, short-TTL, hashed at rest — mirrors promo_tokens.codeHash's
//     "never the plaintext" posture exactly (token_hash here, never the raw
//     32-byte token).
//   email_ingests — the audit row (rule #10 spirit extended: "never silence
//     the cost meter" applies to every gated write path, not just Claude
//     calls). Written on EVERY inbound attempt that reaches the ingest
//     handler, success or honest-partial or gate-failure, so the full
//     ladder (full/partial/resolve, or a 403 reason) has a permanent,
//     query-able record — never only the successes.
//
// SECRETS DISCIPLINE (rule #9 spirit, applied like promo_tokens.codeHash and
// login_links.token_hash): NO plaintext token/magic-link value is EVER
// written to these tables or logged — only their SHA-256 hex digests
// (token_hash columns, 64 chars). The plaintext token is shown to the user
// exactly once (user_email_tokens: rendered as `mira+{token}@…` in Settings
// at generation/regeneration time only); the plaintext magic-link token
// exists only inside the just-sent receipt email, never persisted.
//
// ADDITIVE ONLY (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool —
// docs/design/gateway-neon.sql mirrors this file exactly. Nothing dropped or
// renamed. NOT applied by this lane (deliverable is the patch + the SQL
// file; the orchestrator applies it on Jason's go).
//
// KNOWN-SENDER GATE (owner's ruling, 2026-07-05: "capitalize on the google
// auth and use that email address as known" — this repo's follow-up lane):
// email_ingests gains ONE additive column, identityVia ('token' |
// 'known_sender'), recording WHICH identity story resolved the sender —
// orthogonal to resolvedVia's own ladder-rung meaning. See identityVia's own
// doc comment below. Additive-only, same posture as every other column in
// this file; mirrored in docs/design/gateway-neon.sql, NOT applied here.
// =============================================================================

/**
 * The per-user secret token for `mira+{token}@<domain>` / `meira+{token}@<domain>`
 * (THE SPELLING ALIAS RULE — both bases route to the same token, same user;
 * the base itself carries no identity, only the token does). Regenerating
 * revokes the old row (revoked_at stamped, kept forever — nothing-
 * unrecoverable) and inserts a fresh one; the worker's lookup is always
 * `WHERE token_hash = ? AND revoked_at IS NULL`.
 */
export const userEmailTokens = pgTable(
  'user_email_tokens',
  {
    id: id(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    /** SHA-256 hex digest (64 chars) of the token — mirrors promo_tokens
     *  .codeHash exactly. The plaintext (5 chars, no-ambiguity base32 per the
     *  design doc — apps/api/lib/emailGatewayToken.ts's own alphabet) is
     *  NEVER stored here, only shown once in Settings at generation/
     *  regeneration time. UNIQUE so the worker's identity lookup is a single
     *  indexed equality read. */
    tokenHash: varchar('token_hash', { length: 64 }).notNull().unique(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
    /** NULL = live (the one row `WHERE revoked_at IS NULL` per user should
     *  ever match). Set the instant a regeneration replaces it — the OLD
     *  address stops working immediately (the design doc's own "warm
     *  warning: the old address stops working" UX), but the row itself is
     *  never deleted — a full history of every token this user has ever
     *  held stays on the record permanently, same contract as
     *  entitlementGrants.revokedAt / promoTokens.disabledAt. */
    revokedAt: timestamp('revoked_at', { withTimezone: true }),
  },
  (t) => ({
    userIdx: index('user_email_tokens_user_idx').on(t.userId),
    /** The hot lookup path (worker parses subaddress -> hash -> this table):
     *  narrows to LIVE tokens only. A user's revoked rows are never a false
     *  match for a new inbound email. */
    liveUserIdx: index('user_email_tokens_user_live_idx').on(t.userId, t.revokedAt),
  }),
);

/**
 * Magic-link login records (P3, design doc "THE FULL GATEWAY ARCHITECTURE").
 * One row per mint. Single-use (consumed_at stamped on redemption, never
 * deleted), short TTL (expires_at, 15 min per the design doc), bound to BOTH
 * the user AND a destination — redeeming never lets the caller supply an
 * arbitrary destination, only ever what THIS row already carries.
 */
export const loginLinks = pgTable(
  'login_links',
  {
    id: id(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    /** SHA-256 hex digest of the 32-byte random magic-link token — mirrors
     *  promo_tokens.codeHash / user_email_tokens.tokenHash exactly. The
     *  plaintext exists only inside the just-sent receipt email, never
     *  written here. */
    tokenHash: varchar('token_hash', { length: 64 }).notNull().unique(),
    /** The deep-link path this token redeems to (e.g. `/jobs/{listingId}`) —
     *  path+query only, never a full origin (GET /auth/link/:token composes
     *  this onto STATIC_ALLOWED_RETURN_ORIGINS[0], the same allowlisted-
     *  origin discipline auth.routes.ts's OAuth callback already enforces;
     *  a destination carrying its own origin would be exactly the open-
     *  redirect shape that discipline exists to prevent). */
    destination: text('destination').notNull(),
    /** 15-min TTL per the design doc, computed at mint time from the
     *  injected clock (never Date.now() — the temporal-service law). Checked
     *  alongside consumed_at at redemption; an expired-but-unconsumed link
     *  is treated identically to a never-issued one. */
    expiresAt: timestamp('expires_at', { withTimezone: true }).notNull(),
    /** NULL = unredeemed. Set the instant redemption succeeds — single-use,
     *  structurally: a second GET with the same token finds consumed_at
     *  already set and is rejected, never a second session mint. Never
     *  deleted (nothing-unrecoverable) — a permanent record of when (and
     *  whether) this link was actually used. */
    consumedAt: timestamp('consumed_at', { withTimezone: true }),
    /** 'email_receipt' today (the only minter) — varchar, not an enum, per
     *  this codebase's house convention (jobs.source, accountKind, etc.) so
     *  a future minting path (v2) is a data value, not a migration. */
    createdVia: varchar('created_via', { length: 24 }).notNull().default('email_receipt'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
  },
  (t) => ({
    userIdx: index('login_links_user_idx').on(t.userId),
    /** Per-user daily-cap enforcement (a mint-time COUNT over createdAt) and
     *  general per-user audit reads both scan by this pair. */
    userCreatedIdx: index('login_links_user_created_idx').on(t.userId, t.createdAt),
  }),
);

/**
 * Audit row for EVERY inbound attempt that reaches the ingest handler —
 * written on success (full/partial completeness), on an honest partial, AND
 * on a gate failure (userId null in that case — the sender was never
 * resolved to an account) — rule #10's "never silence the cost meter"
 * extended structurally to this whole surface, not just Claude calls.
 * Never deleted (nothing-unrecoverable) — the permanent record of what this
 * door has ever done.
 */
export const emailIngests = pgTable(
  'email_ingests',
  {
    id: id(),
    /** Nullable: a gate failure (bad token, DMARC fail, From-mismatch) never
     *  resolves to a user — an honest null, never a guessed/default account.
     *  No FK cascade (a user deletion should not cascade-delete their own
     *  audit trail of attempted forwards — mirrors errorLogs' own no-cascade
     *  posture for audit-adjacent tables). */
    userId: uuid('user_id').references(() => users.id),
    fromDomain: text('from_domain'),
    dmarcPass: boolean('dmarc_pass'),
    /** 'full' | 'partial' — mirrors the design doc's walled-source ladder
     *  vocabulary exactly ("the ingest result carries completeness: full |
     *  partial"). Nullable: a gate-failure row never reached the ladder at
     *  all, so completeness is honestly unset, not fabricated as 'partial'. */
    completeness: varchar('completeness', { length: 12 }),
    /** 'body' | 'employer_board' | 'user_capture' | 'gate_failure' — the
     *  ladder rung (or the reason processing never started). varchar per
     *  house convention (a future rung is a data value, not a migration). */
    resolvedVia: varchar('resolved_via', { length: 24 }),
    /** KNOWN-SENDER GATE (owner's ruling, 2026-07-05). 'token' | 'known_sender'
     *  — WHICH identity story resolved this sender to a user, orthogonal to
     *  resolvedVia's own ladder-rung meaning (resolvedVia describes how the
     *  JOB was resolved once identity was already established; this column
     *  describes how the SENDER was). Nullable: a gate-failure row (identity
     *  never resolved at all) is honestly null here, never a guessed value —
     *  NO INVENTION applies to audit rows exactly as it does to user-facing
     *  content. A brand-new additive column rather than overloading
     *  resolvedVia's existing 'gate_failure'/ladder-rung vocabulary, which
     *  every existing test and the design doc's own audit-row spec already
     *  depend on unchanged. varchar per house convention (a future identity
     *  path is a data value, not a migration). */
    identityVia: varchar('identity_via', { length: 16 }),
    /** The lake listing this ingest landed on (job_listings.id), when one
     *  exists — text (not a typed FK) matching this codebase's own
     *  convention for a cross-cutting "which job" pointer that isn't always
     *  populated (jobs.canonicalUrl-adjacent tables lean the same way);
     *  null for a gate-failure or a rung-3 honest-partial row that never
     *  reached a lake write. */
    listingId: text('listing_id'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
  },
  (t) => ({
    userIdx: index('email_ingests_user_idx').on(t.userId),
    createdIdx: index('email_ingests_created_idx').on(t.createdAt),
  }),
);
